<template>
  <div
    v-show="isVisible"
    class="absolute w-full top-full left-0 mt-2 p-4 rounded-lg shadow-lg bg-white z-50 select-none"
    data-pw-strength
  >
    <!-- Title -->
    <div class="text-slate-700 font-semibold mb-2">Sila hesla</div>

    <!-- Strength bar -->
    <div class="h-2 w-full bg-slate-200 rounded">
      <div
        class="h-2 rounded transition-all duration-300"
        :class="strengthBarColor"
        :style="{ width: `${strengthPercentage}%` }"
      />
    </div>

    <!-- Strength verdict -->
    <div class="text-sm text-slate-500 mt-2">
      {{ strengthLabel }}
    </div>

    <!-- Divider -->
    <div class="h-px bg-slate-200 my-4" />

    <!-- Rules title -->
    <div class="text-slate-700 font-semibold mb-2">Pravidlá hesla</div>

    <!-- Rules list -->
    <ul class="space-y-2 text-slate-700">
      <li
        v-for="rule in ruleItems"
        :key="rule.msg"
        class="flex items-start gap-2 text-sm"
      >
        <span
          class="mt-1 inline-block h-2 w-2 rounded-full"
          :class="rule.passed ? 'bg-emerald-500' : 'bg-slate-300'"
          aria-hidden="true"
        />
        <span>{{ rule.msg }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, type Ref } from "vue";

// Types
interface Policy {
  value: number;
  error: string;
}

interface ActivePolicy {
  name: string;
  policy: Policy;
}

interface RuleItem {
  msg: string;
  passed: boolean;
}

// Global window interface
declare global {
  interface Window {
    kcActivePolicies?: ActivePolicy[];
  }
}

// Reactive state
const password: Ref<string> = ref("");
const isVisible: Ref<boolean> = ref(false);
const activePolicies: Ref<ActivePolicy[]> = ref([]);
let passwordInput: HTMLInputElement | null = null;

// Policy validation functions
const policies = {
  length: (policy: Policy, value: string) => {
    if (value.length < policy.value) {
      return templateError(policy);
    }
  },
  maxLength: (policy: Policy, value: string) => {
    if (value.length > policy.value) {
      return templateError(policy);
    }
  },
  upperCase: (policy: Policy, value: string) => {
    if (
      value
        .split("")
        .filter(
          (char) => char === char.toUpperCase() && char !== char.toLowerCase(),
        ).length < policy.value
    ) {
      return templateError(policy);
    }
  },
  lowerCase: (policy: Policy, value: string) => {
    if (
      value
        .split("")
        .filter(
          (char) => char === char.toLowerCase() && char !== char.toUpperCase(),
        ).length < policy.value
    ) {
      return templateError(policy);
    }
  },
  digits: (policy: Policy, value: string) => {
    const digits = value.split("").filter((char) => char.match(/\d/));
    if (digits.length < policy.value) {
      return templateError(policy);
    }
  },
  specialChars: (policy: Policy, value: string) => {
    const specialChars = value.split("").filter((char) => char.match(/\W/));
    if (specialChars.length < policy.value) {
      return templateError(policy);
    }
  },
};

// Utility functions
const templateError = (policy: Policy): string =>
  policy.error.replace("{0}", String(policy.value));

const loadKcPolicies = (): ActivePolicy[] => {
  if (Array.isArray(window.kcActivePolicies)) {
    return window.kcActivePolicies;
  }

  const el = document.getElementById("kc-active-policies");
  if (el?.textContent) {
    try {
      return JSON.parse(el.textContent.trim());
    } catch {
      // Ignore parsing errors
    }
  }

  return [];
};

const validatePassword = (
  pwd: string,
  activePoliciesList: ActivePolicy[],
): string[] => {
  const errors: string[] = [];

  for (const p of activePoliciesList) {
    const validationFn = (policies as any)[p.name];
    if (validationFn) {
      const validationError = validationFn(p.policy, pwd);
      if (validationError) {
        errors.push(validationError);
      }
    }
  }

  return errors;
};

// Find password input in the DOM
const findPasswordInput = (): HTMLInputElement | null => {
  // Look for password input in the same container or parent elements
  const currentElement =
    document.querySelector("[data-pw-strength]")?.parentElement;
  if (!currentElement) return null;

  // Try to find password input in the same container
  let input = currentElement.querySelector<HTMLInputElement>(
    'input[type="password"]',
  );
  if (input) return input;

  // Try to find by common selectors
  input = currentElement.querySelector<HTMLInputElement>(
    'input[name="password"], #password, [data-kc-password]',
  );
  if (input && input.type === "password") return input;

  // Look in parent container
  const parent = currentElement.parentElement;
  if (parent) {
    input = parent.querySelector<HTMLInputElement>('input[type="password"]');
    if (input) return input;
  }

  return null;
};

// Event handlers
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  password.value = target.value || "";

  // Show panel on first keystroke if input is focused
  if (!isVisible.value && document.activeElement === target) {
    isVisible.value = true;
  }
};

const handleFocus = (event: Event) => {
  const target = event.target as HTMLInputElement;
  password.value = target.value || "";
  isVisible.value = true;
};

const handleBlur = () => {
  isVisible.value = false;
};

// Computed properties
const ruleItems = computed((): RuleItem[] => {
  const failing = new Set(
    validatePassword(password.value, activePolicies.value),
  );

  return activePolicies.value.map((p) => {
    const msg = p.policy.error.replace("{0}", String(p.policy.value));
    return {
      msg,
      passed: !failing.has(msg),
    };
  });
});

const passedRulesCount = computed(
  (): number => ruleItems.value.filter((rule) => rule.passed).length,
);

const totalRulesCount = computed((): number => ruleItems.value.length || 1);

const strengthPercentage = computed(
  (): number => (passedRulesCount.value / totalRulesCount.value) * 100,
);

const strengthBarColor = computed((): string => {
  const passed = passedRulesCount.value;
  const total = totalRulesCount.value;

  if (passed === total) {
    return "bg-emerald-500";
  } else if (passed >= Math.ceil(total * 0.5)) {
    return "bg-amber-500";
  } else {
    return "bg-rose-500";
  }
});

const strengthLabel = computed((): string => {
  const passed = passedRulesCount.value;
  const total = totalRulesCount.value;

  if (passed === total) {
    return "Silné";
  } else if (passed >= Math.ceil(total * 0.5)) {
    return "Stredné";
  } else {
    return "Slabé";
  }
});

// Lifecycle
onMounted(() => {
  // Load Keycloak policies
  activePolicies.value = loadKcPolicies();

  // Find and bind to password input
  setTimeout(() => {
    passwordInput = findPasswordInput();

    if (passwordInput) {
      // Set the width to match the input

      // Bind event listeners
      passwordInput.addEventListener("input", handleInput);
      passwordInput.addEventListener("focus", handleFocus);
      passwordInput.addEventListener("blur", handleBlur);

      // Initialize with current value if any
      if (passwordInput.value) {
        password.value = passwordInput.value;
      }
    }
  }, 100); // Small delay to ensure DOM is ready
});

onUnmounted(() => {
  // Clean up event listeners
  if (passwordInput) {
    passwordInput.removeEventListener("input", handleInput);
    passwordInput.removeEventListener("focus", handleFocus);
    passwordInput.removeEventListener("blur", handleBlur);
  }
});
</script>
